import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export interface SlaPolicyConsent {
  accepted: boolean;
  acceptedAt?: string;
  acceptedBy?: string;
  agreedUptimeTarget: number;
  agreedMaxLatencyMs: number;
  agreedResponseMins: number;
  checkSla: boolean;
  checkPrivacyAct: boolean;
  checkLiability: boolean;
}

@Component({
  selector: 'app-policy-consent-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="space-y-5 text-slate-800 font-sans">
      <!-- Header Banner -->
      <div class="p-4 rounded-2xl bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 border border-emerald-200 flex items-start justify-between gap-3 shadow-xs">
        <div class="flex items-start gap-3">
          <div class="p-2.5 rounded-xl bg-emerald-100 text-emerald-700 border border-emerald-300 shrink-0">
            <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <div>
            <div class="flex items-center gap-2 flex-wrap">
              <h3 class="text-base font-extrabold text-slate-900">{{ title }}</h3>
              @if (isReadOnly) {
                <span class="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-100 text-emerald-800 border border-emerald-300 uppercase tracking-wider flex items-center gap-1">
                  <span class="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse"></span>
                  Active & Binding
                </span>
              } @else {
                <span class="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-amber-100 text-amber-800 border border-amber-300 uppercase tracking-wider">
                  Action Required
                </span>
              }
            </div>
            <p class="text-xs text-slate-600 mt-1 leading-relaxed">
              @if (isReadOnly) {
                Official copy of the Service Level Agreement (SLA) and compliance terms agreed upon by <strong>{{ institutionName }}</strong>.
              } @else {
                As an authorized representative of <strong>{{ institutionName }}</strong>, you must explicitly review and accept the official Service Level Agreements (SLA) and legal obligations before accessing any platform components.
              }
            </p>
          </div>
        </div>

        @if (isReadOnly) {
          <button (click)="closed.emit()" class="p-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200 transition" title="Close">
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        }
      </div>

      <!-- Read-Only Audit Signature Card -->
      @if (isReadOnly && (agreedBy || agreedAt)) {
        <div class="p-3.5 rounded-xl bg-slate-50 border border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
          <div class="flex items-center gap-2 text-slate-700">
            <svg class="w-4 h-4 text-emerald-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span><strong>Signed By:</strong> {{ agreedBy || 'Authorized Officer' }}</span>
          </div>
          <div class="flex items-center gap-2 text-slate-500 font-mono text-[11px]">
            <svg class="w-4 h-4 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{{ agreedAt || 'Recorded in system' }}</span>
          </div>
        </div>
      }

      <!-- SLA Targets Metrics Summary Grid -->
      <div class="grid grid-cols-3 gap-3 text-center">
        <div class="p-3 rounded-xl bg-white border border-slate-200 shadow-xs">
          <div class="text-[10px] uppercase font-bold text-slate-500">Baseline Uptime Target</div>
          <div class="text-base font-black text-emerald-700 mt-0.5">99.90%</div>
          <div class="text-[9px] text-slate-400">24/7 Availability</div>
        </div>
        <div class="p-3 rounded-xl bg-white border border-slate-200 shadow-xs">
          <div class="text-[10px] uppercase font-bold text-slate-500">Max Endpoint Latency</div>
          <div class="text-base font-black text-sky-700 mt-0.5">&le; 250ms</div>
          <div class="text-[9px] text-slate-400">Realtime Throughput</div>
        </div>
        <div class="p-3 rounded-xl bg-white border border-slate-200 shadow-xs">
          <div class="text-[10px] uppercase font-bold text-slate-500">Incident SLA Window</div>
          <div class="text-base font-black text-purple-700 mt-0.5">15 Mins</div>
          <div class="text-[9px] text-slate-400">Auto Escalation to DPO</div>
        </div>
      </div>

      <!-- Legal Policy Scrollable Box -->
      <div class="p-4 rounded-xl bg-slate-50 border border-slate-200 max-h-40 overflow-y-auto text-xs text-slate-700 space-y-2 leading-relaxed custom-scrollbar">
        <div class="font-bold text-emerald-800 uppercase tracking-wider text-[10px]">ZAN-X Change Policy Excerpt (Act No. 14 / e-Gov Code):</div>
        <p>1. <strong>Purpose Restriction:</strong> Data Consumers are strictly bound to consume data endpoints solely for the approved, declared public purpose. Secondary data mining or unauthorized redistribution is strictly prohibited and punishable under Zanzibar law.</p>
        <p>2. <strong>Custodian Performance:</strong> Data Custodians warrant to maintain service endpoints adhering to the baseline SLA uptime standards (&ge;99.90%) and latency targets (&le;250ms).</p>
        <p>3. <strong>Audit & Regulatory Supervision:</strong> All API transactions and payload handshakes are logged into an immutable security trail audited regularly by the e-Government Agency of Zanzibar (eGAZ) and the Data Protection Commission.</p>
      </div>

      <!-- Compliance Checkboxes / Read-Only Badges -->
      <div class="space-y-2.5 pt-2 border-t border-slate-200 text-xs">
        <label [ngClass]="isReadOnly ? 'bg-emerald-50/50 border-emerald-200 cursor-default' : 'bg-white border-slate-200 hover:border-emerald-300 cursor-pointer'" class="flex items-start gap-3 p-2.5 rounded-xl border transition shadow-xs">
          <input
            type="checkbox"
            [(ngModel)]="checkSla"
            [disabled]="isReadOnly"
            class="mt-0.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
          />
          <span class="text-slate-800">
            <strong class="text-emerald-700">SLA Standard Compliance:</strong> Our institution agrees to comply with the e-Gov Data Sharing SLA Standards, uptime targets (99.9%), and latency thresholds (&le;250ms).
          </span>
        </label>

        <label [ngClass]="isReadOnly ? 'bg-emerald-50/50 border-emerald-200 cursor-default' : 'bg-white border-slate-200 hover:border-emerald-300 cursor-pointer'" class="flex items-start gap-3 p-2.5 rounded-xl border transition shadow-xs">
          <input
            type="checkbox"
            [(ngModel)]="checkPrivacyAct"
            [disabled]="isReadOnly"
            class="mt-0.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
          />
          <span class="text-slate-800">
            <strong class="text-emerald-700">Data Protection Mandate:</strong> I certify that all requested data attributes will be processed strictly in accordance with Zanzibar Data Protection & e-Gov Legislation (Sec. 14 & 18).
          </span>
        </label>

        <label [ngClass]="isReadOnly ? 'bg-emerald-50/50 border-emerald-200 cursor-default' : 'bg-white border-slate-200 hover:border-emerald-300 cursor-pointer'" class="flex items-start gap-3 p-2.5 rounded-xl border transition shadow-xs">
          <input
            type="checkbox"
            [(ngModel)]="checkLiability"
            [disabled]="isReadOnly"
            class="mt-0.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
          />
          <span class="text-slate-800">
            <strong class="text-purple-700">Audit & Penalty Acceptance:</strong> I acknowledge that policy breaches, unauthorized harvesting, or SLA violations result in immediate API credential revocation and legal audit escalation.
          </span>
        </label>
      </div>

      <!-- Action Footer -->
      <div class="pt-3 border-t border-slate-200 flex items-center justify-between">
        @if (isReadOnly) {
          <div class="text-[11px] text-emerald-700 font-bold flex items-center gap-1.5">
            <svg class="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            <span>All Compliance Mandates Confirmed</span>
          </div>

          <button
            (click)="closed.emit()"
            class="px-5 py-2.5 rounded-xl text-xs font-bold bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-300 transition cursor-pointer"
          >
            Close Review
          </button>
        } @else {
          <div class="text-[11px] text-slate-500">
            <span *ngIf="!isAllChecked()" class="text-amber-600 font-bold">⚠️ All 3 checkboxes required to proceed</span>
            <span *ngIf="isAllChecked()" class="text-emerald-600 font-bold">✓ Policy Requirements Satisfied</span>
          </div>

          <button
            [disabled]="!isAllChecked()"
            (click)="confirmAcceptance()"
            [ngClass]="isAllChecked() 
              ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-500 hover:to-teal-500 shadow-md shadow-emerald-600/20 cursor-pointer' 
              : 'bg-slate-200 text-slate-400 cursor-not-allowed border border-slate-300'"
            class="px-5 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2"
          >
            <span>Accept SLA & Proceed</span>
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </button>
        }
      </div>
    </div>
  `
})
export class PolicyConsentModalComponent implements OnInit {
  @Input() institutionName = 'Data Consumer Node';
  @Input() isReadOnly = false;
  @Input() agreedAt?: string;
  @Input() agreedBy?: string;
  @Input() title = 'Mandatory e-Gov Data Sharing SLA & Policy Agreement';

  @Output() accepted = new EventEmitter<SlaPolicyConsent>();
  @Output() closed = new EventEmitter<void>();

  checkSla = false;
  checkPrivacyAct = false;
  checkLiability = false;

  ngOnInit() {
    if (this.isReadOnly) {
      this.checkSla = true;
      this.checkPrivacyAct = true;
      this.checkLiability = true;
    }
  }

  isAllChecked(): boolean {
    return this.checkSla && this.checkPrivacyAct && this.checkLiability;
  }

  confirmAcceptance() {
    if (!this.isAllChecked()) return;
    const consent: SlaPolicyConsent = {
      accepted: true,
      acceptedAt: new Date().toISOString().substring(0, 16).replace('T', ' '),
      acceptedBy: `${this.institutionName} Authorized Officer`,
      agreedUptimeTarget: 99.90,
      agreedMaxLatencyMs: 250,
      agreedResponseMins: 15,
      checkSla: true,
      checkPrivacyAct: true,
      checkLiability: true
    };
    this.accepted.emit(consent);
  }
}
